import { Injectable } from '@angular/core';
import { Firestore, collection, addDoc, getDocs, Unsubscribe, query, where, orderBy, limit, updateDoc, doc, collectionSnapshots, collectionData, collectionChanges } from '@angular/fire/firestore';
import { DatePipe } from '@angular/common';
import { UserService } from 'projects/chat-common/src/lib/services/user.service';
import { Period } from 'projects/chat-common/src/lib/models/period';
import { firstValueFrom, map, Observable } from 'rxjs';
import { DiaryService } from './diary.service';
import { UserInfoService } from './user.info.service';

@Injectable({ providedIn: 'root' })
export class PeriodService {
  private unsubscribe: Unsubscribe | null = null;

  constructor(
    private firestore: Firestore,
    private userService: UserService,
    private datePipe: DatePipe,
    private diaryService: DiaryService,
    private userInfoService: UserInfoService,
  ) { }

  public hasActivePeriod$ = collectionData(
    query(
      collection(this.firestore, `v0/data/user/${this.userService.user.id}/periods`),
      where('endedOn', '==', null),
      limit(1)
    )
  ).pipe(
    map(docs => docs.length > 0)
  );

  public periodChanges$ = collectionChanges(
    query(collection(this.firestore, `v0/data/user/${this.userService.user.id}/periods`), orderBy('startedOn'))
  ).pipe(
    map(changes => changes.map(c => ({
      type: c.type,
      id: c.doc.id,
      ...(c.doc.data() as any)
    })))
  );

  public get pregnancyChanges$() {
    return collectionData(
      query(
        collection(this.firestore, `v0/data/user/${this.userService.user.id}/pregnancies`),
        orderBy('startDate', 'desc')
      ),
      { idField: 'id' }
    ) as unknown as Observable<
      { id: string; startDate: string; endDate: string | null; dueDate: string }[]
    >;
  }

  // TODO: pregnancy service or rename this to reproductive health service
  public hasActivePregnancy$ = collectionData(
    query(
      collection(this.firestore, `v0/data/user/${this.userService.user.id}/pregnancies`),
      where('endDate', '==', null),
      limit(1)
    )
  ).pipe(
    map(docs => docs.length > 0)
  );

  // TODO: another service
  public hasSleepData$ = collectionData(
    query(
      collection(this.firestore, `v0/data/user/${this.userService.user.id}/wellness/well-agg/well-agg-daily`),
    ),
  ).pipe(
    map(docs => docs.length > 0)
  );

  private formatDate(d: string | Date): string {
    if (d instanceof Date) {
      return this.datePipe.transform(d, 'yyyy-MM-dd')!;
    }
    return d;
  }

  private daysBetween(a: string, b: string): number {
    const d1 = new Date(a).getTime();
    const d2 = new Date(b).getTime();
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  }

  private median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
      : sorted[mid];
  }

  private isOverlapping(selected: Period, periods: Period[], length: number): boolean {
    const start = new Date(selected.startedOn);
    const end = selected.endedOn
      ? new Date(selected.endedOn)
      : new Date(start.getTime() + length * 24 * 60 * 60 * 1000);

    if (end < start) return false;

    for (const p of periods) {
      const pStart = new Date(p.startedOn);
      const pEnd = p.endedOn ? new Date(p.endedOn) : new Date();

      if (pStart >= start && pStart <= end) return true;
      if (pEnd >= start && pEnd <= end) return true;
    }

    return false;
  }

  public async addPeriod(period: Period): Promise<boolean> {
    const colRef = collection(this.firestore, `v0/data/user/${this.userService.user.id}/periods`);
    const snap = await getDocs(colRef);
    const existing: Period[] = snap.docs.map(d => d.data() as Period);

    const lengths = existing
      .filter(p => p.endedOn)
      .map(p => this.daysBetween(p.startedOn, p.endedOn!));
    const median = this.median(lengths);

    if (this.isOverlapping(period, existing, median || 5)) {
      return false;
    }

    const formatted: Period = {
      startedOn: this.formatDate(period.startedOn),
      endedOn: period.endedOn ? this.formatDate(period.endedOn) : null,
    };

    await addDoc(colRef, formatted);
    return true;
  }

  public async stopPeriod(): Promise<boolean> {
    const userId = this.userService.user.id;
    const colRef = collection(this.firestore, `v0/data/user/${userId}/periods`);

    const q = query(
      colRef,
      where('endedOn', '==', null),
      orderBy('startedOn', 'desc'),
      limit(1)
    );

    const snap = await getDocs(q);
    if (snap.empty) {
      return false;
    }

    const openDoc = snap.docs[0];
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd')!;

    await updateDoc(doc(this.firestore, colRef.path, openDoc.id), { endedOn: today });
    return true;
  }

  private formatWeeksDays(totalDays: number): string {
    const weeks = Math.floor(totalDays / 7);
    const days = totalDays % 7;
    return `${weeks}w${days}d`;
  }

  private getPregnancyOrPostpartumState(
    pregnancy: { startDate?: string | Date | null; endDate?: string | Date | null; dueDate?: string | Date | null },
    lastPeriod?: { startedOn?: string | null }
  ): { state: "Pregnancy" | "Postpartum"; gestationalAge: string; trimester?: "1st Trimester" | "2nd Trimester" | "3rd Trimester" } | null {
    if (!pregnancy) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const toDate = (d?: string | Date | null) => d ? new Date(d) : null;

    if (!pregnancy.endDate) {
      let totalDays: number | null = null;

      if (pregnancy.dueDate) {
        const due = toDate(pregnancy.dueDate);
        if (due) {
          const diff = Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          totalDays = 280 - diff;
        }
      } else if (pregnancy.startDate) {
        const start = toDate(pregnancy.startDate);
        if (start) {
          totalDays = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }
      }

      if (totalDays != null && totalDays >= 0) {
        let trimester: "1st Trimester" | "2nd Trimester" | "3rd Trimester";
        if (totalDays < 98) {
          trimester = "1st Trimester";
        } else if (totalDays < 196) {
          trimester = "2nd Trimester";
        } else {
          trimester = "3rd Trimester";
        }
        return {
          state: "Pregnancy",
          gestationalAge: this.formatWeeksDays(totalDays),
          trimester
        };
      }
    }

    const end = toDate(pregnancy.endDate);
    if (end) end.setHours(0, 0, 0, 0);

    if (end) {
      const periodStart = toDate(lastPeriod?.startedOn);
      const hasPeriodAfterPregnancy = periodStart && periodStart > end;
      if (!hasPeriodAfterPregnancy) {
        const diff = Math.floor((today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
        if (diff >= 0) {
          return {
            state: "Postpartum",
            gestationalAge: this.formatWeeksDays(diff)
          };
        }
      }
    }

    return null;
  }


  async calculatePhase(): Promise<{ state: string; day?: string, trimester?: string }> {
    const [period, rh, pregnancies] = await Promise.all([
      this.diaryService.getLastOpenPeriod(),
      this.userInfoService.getReproductiveHealth(),
      firstValueFrom(this.pregnancyChanges$)
    ]);

    if (!period || !rh || !pregnancies) return { state: 'No data', day: null };

    const activePregnancy = (Array.isArray(pregnancies) ? pregnancies : [pregnancies])?.[0];

    const pregState = this.getPregnancyOrPostpartumState(activePregnancy, period);
    if (pregState) {
      return { state: pregState.state, day: pregState.gestationalAge, trimester: pregState.trimester };
    }

    const periodDays =
      (rh as any).periodLengthPredicted ?? (rh as any).periodLength ?? null;
    const cycleDays =
      (rh as any).cycleLengthPredicted ?? (rh as any).cycleLength ?? null;

    const res = this.diaryService.getPeriodPhase(period, periodDays, cycleDays);
    const day = this.diaryService.getCycleDay(period, cycleDays);

    return typeof res === 'string'
      ? { state: res, day: day?.toString() }
      : { state: res?.state ?? 'No data', day: null };
  }

  calculateEndDate(startDate: Date): Date | null {
    const today = new Date();

    const diffInMs = today.getTime() - startDate.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInDays > 7) {
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 7);
      return endDate;
    }

    return null;
  }

}
